import * as _ from "lodash";
import { Box, Button } from "@mui/joy";
import { useCallback, useEffect, useRef, useState } from "react";
import { ProgramContentType } from "data/BlockComponent";
import { useRecoilState } from "recoil";
import { permanentDataAtom } from "recoil/permanentDataAtom";
import useGetCertainEventFromApp from "hooks/useGetCertainEventFromApp";
import { setProgramContentData } from "logic/logics";
import AudioProgressBar from "./AudioProgressBar";
import { theme } from "styles/theme";
import { activeAudioPlayerAtom } from "recoil/activeAudioPlayerAtom";
import { PauseIcon, PlayIcon } from "./AudioButtonStatus";

export default function AudioButtonWeb({
  audioInstanceKey,
  audioUrl,
  initialTime,
  setData,
  blockIndex,
  lineIndex,
  cellIndex,
  isCoach,
  title,
  artist,
  artwork,
  extraAudioUrl,
  extraLoopNumber, // 안씀
  readonly,
}: {
  audioInstanceKey: string;
  audioUrl: string;
  initialTime: number;
  setData: React.Dispatch<
    React.SetStateAction<ProgramContentType[] | undefined>
  >;
  blockIndex: number;
  lineIndex: number;
  cellIndex: number;
  isCoach: boolean;
  title?: string;
  artist?: string;
  artwork?: string;
  extraAudioUrl?: string;
  extraLoopNumber?: number; // 안씀
  readonly: boolean;
}) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const extraAudioRef = useRef<HTMLAudioElement>(null);

  const animationFrameRef = useRef<number | null>(null);
  const liveSessionStartTimeRef = useRef<number | null>(null);
  const liveSessionStartCurrentTimeRef = useRef<number | null>(null);

  const [duration, setDuration] = useState<number>(0);
  const [currentTime, setCurrentTime] = useState<number>(initialTime);
  const [isSeeking, setIsSeeking] = useState(false);

  const [activeAudioKey, setActiveAudioKey] = useRecoilState(
    activeAudioPlayerAtom
  );
  const isPlaying = activeAudioKey === audioInstanceKey;

  const isLive = !!extraAudioUrl && duration > 0 && currentTime >= duration;

  useEffect(() => {
    if (isPlaying) {
      return;
    }

    setCurrentTime(initialTime);
    if (!audioRef.current) return;

    if (initialTime >= duration && duration > 0) {
      audioRef.current.currentTime = duration;
      if (extraAudioRef.current) {
        extraAudioRef.current.currentTime = 0;
      }
    } else {
      audioRef.current.currentTime = initialTime;
      if (extraAudioRef.current) {
        extraAudioRef.current.currentTime = 0;
      }
    }
  }, [initialTime, duration, isPlaying]);

  const updateCurrentTimeData = useCallback(() => {
    const updatedTime = currentTime;
    const isTextareaFocused = document.activeElement?.tagName === "TEXTAREA";
    const isEnded =
      duration > 0 &&
      currentTime > 0 &&
      duration !== currentTime &&
      duration - 30 <= currentTime;

    if (!isTextareaFocused || isEnded) {
      setProgramContentData({
        setData,
        blockIndex,
        lineIndex,
        cellIndex,
        newlyAddedData: {
          currentTime: updatedTime,
          ...(updatedTime > 0 && {
            id: "meditation_time",
            value: `${Math.floor((updatedTime + 10 || 60) / 60)}`,
          }),
          ...(isEnded && {
            isEnded: true,
          }),
        },
      });
    }
  }, [currentTime, duration, setData, blockIndex, lineIndex, cellIndex]);

  useEffect(() => {
    if (isPlaying && currentTime > 0 && Math.floor(currentTime) % 5 === 0) {
      updateCurrentTimeData();
    }
  }, [currentTime, isPlaying, updateCurrentTimeData]);

  useEffect(() => {
    const stopTimer = () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };

    if (isPlaying && isLive && !isSeeking) {
      const tick = () => {
        if (
          liveSessionStartTimeRef.current === null ||
          liveSessionStartCurrentTimeRef.current === null
        ) {
          liveSessionStartTimeRef.current = performance.now();
          liveSessionStartCurrentTimeRef.current = currentTime;
        }

        const elapsed = performance.now() - liveSessionStartTimeRef.current;
        const newTotalTime =
          liveSessionStartCurrentTimeRef.current + elapsed / 1000;

        setCurrentTime(newTotalTime);

        animationFrameRef.current = requestAnimationFrame(tick);
      };

      stopTimer();
      tick();
    } else {
      stopTimer();
    }

    return stopTimer;
  }, [isPlaying, isLive, currentTime, duration, isSeeking]);

  const start = useCallback(() => {
    if (isLive && extraAudioRef.current) {
      extraAudioRef.current.play();
    } else if (audioRef.current) {
      audioRef.current.play();
    }
    setActiveAudioKey(audioInstanceKey);
  }, [audioInstanceKey, isLive, setActiveAudioKey]);

  const stop = useCallback(() => {
    audioRef.current?.pause();
    extraAudioRef.current?.pause();
    updateCurrentTimeData();

    if (activeAudioKey === audioInstanceKey) {
      setActiveAudioKey(null);
    }
  }, [
    updateCurrentTimeData,
    activeAudioKey,
    audioInstanceKey,
    setActiveAudioKey,
  ]);

  useEffect(() => {
    if (isPlaying) {
      liveSessionStartTimeRef.current = null;
      liveSessionStartCurrentTimeRef.current = null;

      if (isLive && extraAudioRef.current) {
        extraAudioRef.current.play();
      } else if (audioRef.current) {
        audioRef.current.play();
      }
    } else {
      audioRef.current?.pause();
      extraAudioRef.current?.pause();
    }
  }, [isPlaying, isLive]);

  useEffect(() => {
    if (activeAudioKey !== audioInstanceKey && isPlaying) {
      stop();
    }
  }, [activeAudioKey, audioInstanceKey, isPlaying, stop]);

  useEffect(() => {
    return () => {
      if (activeAudioKey === audioInstanceKey) {
        setActiveAudioKey(null);
      }
    };
  }, [activeAudioKey, audioInstanceKey, setActiveAudioKey]);

  const setAudioEnded = useCallback(() => {
    setData((data) => {
      if (data) {
        const data_temp = _.cloneDeep(data);
        const currentContent =
          data_temp[blockIndex].lines[lineIndex][cellIndex].content || {};

        data_temp[blockIndex].lines[lineIndex][cellIndex].content = {
          ...currentContent,
          isEnded: true,
        };
        if (!_.isEqual(data, data_temp)) {
          return data_temp;
        }
        return data;
      }
      return data;
    });
  }, [setData, blockIndex, lineIndex, cellIndex]);

  const handleEnded = useCallback(() => {
    if (extraAudioUrl && isPlaying) {
      extraAudioRef.current?.play();
      if (!isCoach) {
        setAudioEnded();
      }
    } else {
      stop();
      if (!isCoach) {
        setAudioEnded();
      }
    }
    updateCurrentTimeData();
  }, [
    extraAudioUrl,
    isPlaying,
    isCoach,
    setAudioEnded,
    stop,
    updateCurrentTimeData,
  ]);

  const handleDurationChange = useCallback(() => {
    if (audioRef.current?.duration) {
      setDuration(audioRef.current.duration);
    }
  }, []);

  const handleLoadedData = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.currentTime = initialTime;
      if (audioRef.current.duration) {
        setDuration(audioRef.current.duration);
      }
    }
  }, [initialTime]);

  const handleTimeUpdate = useCallback(() => {
    if (isSeeking || isLive) return;
    if (audioRef.current) {
      const newCurrentTime = audioRef.current.currentTime;
      setCurrentTime(newCurrentTime);
      if (
        !isCoach &&
        newCurrentTime > 0 &&
        duration > 0 &&
        newCurrentTime >= duration
      ) {
        setAudioEnded();
      }
    }
  }, [isSeeking, isLive, isCoach, duration, setAudioEnded]);

  const handleSeekStart = useCallback(() => {
    setIsSeeking(true);
    // Pause audio temporarily for smooth seeking
    audioRef.current?.pause();
    extraAudioRef.current?.pause();
  }, []);

  const handleSeekChange = useCallback((newTime: number) => {
    // While dragging, just update the visual time
    setCurrentTime(newTime);
  }, []);

  const handleSeekEnd = useCallback(
    (newTime: number) => {
      // Reset virtual timer baseline
      liveSessionStartTimeRef.current = null;
      liveSessionStartCurrentTimeRef.current = null;

      // Set the actual audio element time
      if (audioRef.current) {
        if (newTime >= duration && duration > 0) {
          audioRef.current.currentTime = duration;
        } else {
          audioRef.current.currentTime = newTime;
        }
      }

      if (isPlaying) {
        start();
      }

      setIsSeeking(false);
    },
    [duration, isPlaying, start]
  );

  const [permanantDataState, setPermanantDataState] =
    useRecoilState(permanentDataAtom);

  const timeSpentInBackgroundEvent = useGetCertainEventFromApp({
    eventName: "timeSpentInBackground",
  });

  useEffect(() => {
    if (timeSpentInBackgroundEvent) {
      const seconds = timeSpentInBackgroundEvent.params.seconds;
      const stored = permanantDataState?.audioCurrentTime;
      if (!isPlaying && stored?.audioInstanceKey === audioInstanceKey) {
        setCurrentTime(stored.currentTime + seconds);
      }
    }
  }, [
    timeSpentInBackgroundEvent,
    isPlaying,
    permanantDataState,
    audioInstanceKey,
  ]);

  const handleVisibilityChange = useCallback(() => {
    if (document.hidden) {
      // 숨겨진 분기(display:none)에도 mount되는 다른 인스턴스들이 키를 덮어쓰지 않도록,
      // 실제 재생 중인 플레이어만 백그라운드 이어듣기 상태를 저장한다.
      if (isPlaying) {
        setPermanantDataState((state) => ({
          ...state,
          audioCurrentTime: { audioInstanceKey, currentTime },
        }));
      }
      if (!isCoach) setAudioEnded();
    }
  }, [
    isPlaying,
    audioInstanceKey,
    currentTime,
    isCoach,
    setAudioEnded,
    setPermanantDataState,
  ]);

  useEffect(() => {
    const audioEl = audioRef.current;

    document.addEventListener("visibilitychange", handleVisibilityChange);
    if (audioEl) {
      audioEl.addEventListener("loadeddata", handleLoadedData);
      audioEl.addEventListener("durationchange", handleDurationChange);
      audioEl.addEventListener("timeupdate", handleTimeUpdate);
      audioEl.addEventListener("ended", handleEnded);
    }

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (audioEl) {
        audioEl.removeEventListener("loadeddata", handleLoadedData);
        audioEl.removeEventListener("durationchange", handleDurationChange);
        audioEl.removeEventListener("timeupdate", handleTimeUpdate);
        audioEl.removeEventListener("ended", handleEnded);
      }
    };
  }, [
    handleVisibilityChange,
    handleLoadedData,
    handleDurationChange,
    handleTimeUpdate,
    handleEnded,
  ]);

  const startRef = useRef(start);
  const stopRef = useRef(stop);

  useEffect(() => {
    startRef.current = start;
    stopRef.current = stop;
  }, [start, stop]);

  // ========================[ 수정된 부분 시작 ]========================
  // 이 useEffect는 현재 오디오 플레이어가 활성화되었을 때 MediaSession을 관리합니다.
  useEffect(() => {
    if (activeAudioKey === audioInstanceKey) {
      if ("mediaSession" in navigator) {
        navigator.mediaSession.metadata = new MediaMetadata({
          title: title || "명상훈련",
          artist: artist || "디스턴싱",
          artwork: artwork
            ? [
                { src: artwork, sizes: "96x96", type: "image/png" },
                { src: artwork, sizes: "512x512", type: "image/png" },
              ]
            : [],
        });

        navigator.mediaSession.setActionHandler("play", () =>
          startRef.current()
        );
        navigator.mediaSession.setActionHandler("pause", () =>
          stopRef.current()
        );
      }

      // cleanup 함수: 플레이어가 비활성화될 때(일시정지 또는 다른 플레이어 재생) 호출됩니다.
      // 'pause' 핸들러만 제거하고 'play' 핸들러는 남겨두어, 미디어 컨트롤에서 다시 재생할 수 있도록 합니다.
      return () => {
        if ("mediaSession" in navigator) {
          if (
            navigator.mediaSession.metadata?.title === (title || "명상훈련")
          ) {
            navigator.mediaSession.setActionHandler("pause", null);
          }
        }
      };
    }
  }, [activeAudioKey, audioInstanceKey, title, artist, artwork]);

  // 이 useEffect는 컴포넌트가 unmount될 때 최종 정리를 담당합니다.
  // 메모리 누수 및 unmount된 컴포넌트의 상태를 변경하려는 시도를 방지합니다.
  useEffect(() => {
    // 컴포넌트가 생성될 때의 title 값을 저장합니다.
    const currentTitle = title || "명상훈련";
    return () => {
      if ("mediaSession" in navigator) {
        // unmount 시점에, MediaSession의 소유자가 여전히 이 컴포넌트인 경우에만 핸들러를 제거합니다.
        // 이를 통해 다른 활성 플레이어의 핸들러를 실수로 제거하는 것을 방지합니다.
        if (navigator.mediaSession.metadata?.title === currentTitle) {
          navigator.mediaSession.setActionHandler("play", null);
          navigator.mediaSession.setActionHandler("pause", null);
        }
      }
    };
  }, [title]); // title이 변경될 수 있으므로 의존성 배열에 추가합니다. []도 가능합니다.
  // ========================[ 수정된 부분 끝 ]========================

  return (
    <Box>
      <audio ref={audioRef} src={audioUrl} style={{ display: "none" }}></audio>
      {extraAudioUrl && (
        <audio
          ref={extraAudioRef}
          src={extraAudioUrl}
          loop
          style={{ display: "none" }}
        ></audio>
      )}

      <AudioProgressBar
        currentTime={currentTime}
        duration={duration}
        onSeekStart={handleSeekStart}
        onSeekChange={handleSeekChange}
        onSeekEnd={handleSeekEnd}
        isPlaying={isPlaying}
        isLive={isLive}
      />

      <Button
        key={`${audioInstanceKey}_${extraAudioUrl || "noExtraAudioUrl"}`}
        variant={isPlaying ? "Outlined" : "Solid"}
        color="primary"
        fullWidth
        onClick={() => {
          if (isPlaying) {
            stop();
          } else {
            start();
          }
        }}
        size={"lg"}
        sx={{
          fontSize: "16px",
          fontWeight: 700,
          justifyContent: "center",
          "& .MuiButton-startDecorator": {
            marginRight: 0,
            color: readonly
              ? theme.vars.palette.Content.SuperWeak
              : isPlaying
                ? theme.vars.palette.Content.Highlight
                : "white",
          },
          gap: 1,
        }}
        disabled={readonly}
        startDecorator={isPlaying ? <PauseIcon /> : <PlayIcon />}
      >
        {isPlaying ? "일시정지" : "재생하기"}
      </Button>
    </Box>
  );
}
